import React from "react";
import { useParams } from "react-router-dom";
import AppTable from "components/Shared/AppTable";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useTablePagination } from "hooks";
import { Chip, Stack, Typography, IconButton, Tooltip } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { useLazyGetVendorLeadQuotationPdfExcelQuery } from "store/services/vendorLeadApi";

const PdfChip = ({ pdf }) => {
    if (!pdf) return <span style={{ color: "#999" }}>—</span>;
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Stack spacing={0.3}>
                <Chip
                    size="small"
                    label={pdf.title ?? pdf.vendorQuotationId}
                    color="error"
                    variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                    {pdf.vendorQuotationId}
                </Typography>
            </Stack>
            {pdf?.pdfAttachment?.url && (
                <Tooltip title="View PDF">
                    <IconButton
                        size="small"
                        onClick={() => window.open(pdf.pdfAttachment.url, "_blank")}
                    >
                        <OpenInNew fontSize="small" color="error" />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
};

const ExcelChip = ({ excel }) => {
    if (!excel) return <span style={{ color: "#999" }}>—</span>;
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Stack spacing={0.3}>
                <Chip
                    size="small"
                    label={excel.title ?? excel.VendorQuotationExcelId}
                    color="success"
                    variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                    {excel.VendorQuotationExcelId}
                </Typography>
            </Stack>
            {excel?.excelAttachment?.url && (
                <Tooltip title="View Excel">
                    <IconButton
                        size="small"
                        onClick={() => window.open(excel.excelAttachment.url, "_blank")}
                    >
                        <OpenInNew fontSize="small" color="success" />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
};

const VendorLeadQuotationPdfExcelTab = () => {
    const { id } = useParams();

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [fetchEntries, { data: entriesRes, isLoading, isFetching }] =
        useLazyGetVendorLeadQuotationPdfExcelQuery();

    React.useEffect(() => {
        fetchEntries({ id, page: currentPage, limit: perPage });
    }, [id, currentPage, perPage, fetchEntries]);

    const columns = [
        {
            name: "Added By",
            selector: (row) => row?.addedBy?.name,
            cell: (row) => (
                <div className="line-clamp-1">
                    {row?.addedBy?.name ?? "—"}
                </div>
            ),
            minWidth: "150px",
        },
        {
            name: "PDF Quotation",
            selector: (row) => row?.vendorQuotationPdf?.title,
            cell: (row) => <PdfChip pdf={row?.vendorQuotationPdf} />,
            minWidth: "220px",
        },
        {
            name: "Excel Quotation",
            selector: (row) => row?.vendorQuotationExcel?.title,
            cell: (row) => <ExcelChip excel={row?.vendorQuotationExcel} />,
            minWidth: "220px",
        },
        {
            name: "Added At",
            selector: (row) => row?.createdAt,
            cell: (row) => (
                <div className="line-clamp-1">
                    {row?.createdAt ? localizeDateTimeShort(row.createdAt) : "N/A"}
                </div>
            ),
            minWidth: "170px",
        },
    ];

    return (
        <AppTable
            columns={columns}
            data={entriesRes?.data}
            progressPending={isLoading || isFetching}
            paginationTotalRows={entriesRes?.totalSize ?? 0}
            paginationPerPage={perPage}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
        />
    );
};

export default VendorLeadQuotationPdfExcelTab;